import { FC, useMemo } from 'react'
import { ReportUnitF } from '@logic/contexts/Learners/LearnersFrontend.type.ts'
import styles from './SkillAreaUnit.module.scss'
import { Line } from 'react-chartjs-2'
import { chartConfig, generateChartOptions } from '@src/pages/Learner/Show/Activities/activityReport/chart.utils.ts'
import { mapStatusToReadableStatus, TaskStatus } from '@src/utils/mapStatusToReadableStatus.ts'
import { cn } from '@src/utils/cn.ts'
import { formatDate } from '@src/utils/dateFormatter'

// Moved outside component to prevent recreation on each render
const chartOptions = generateChartOptions('% of correct answers', 100) as any

const DURATION_MAP = {
  30: 'month',
  90: '3 months',
  365: 'year',
} as const

type Props = {
  unit: ReportUnitF
  learner: { fullName: string }
  duration: string
}

const safeTime = (d?: string | null) => {
  const t = d ? new Date(d).getTime() : NaN
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY
}

export const SkillAreaUnit: FC<Props> = ({ unit, learner, duration }) => {
  const getDurationText = (days: string | number): string => {
    const numDays = typeof days === 'string' ? parseInt(days, 10) : days
    return DURATION_MAP[numDays as keyof typeof DURATION_MAP] || `${numDays} days`
  }

  const formatScore = (score: string | number): string => {
    const numScore = typeof score === 'string' ? parseFloat(score) : score
    return !isNaN(numScore) ? Math.floor(numScore).toString() : '-'
  }

  const { progressStory, chartData, formattedDates } = useMemo(() => {
    const sessionCount = unit.activityCount || 0
    const firstScore = formatScore(unit.firstActivityScore || '')
    const durationText = getDurationText(duration)
    const readableStatus = mapStatusToReadableStatus(unit.status as TaskStatus)

    const firstActivityDate = unit.firstActivityDate ? formatDate(unit.firstActivityDate, false) : '-'
    const lastPracticed = unit.lastPracticed ? formatDate(unit.lastPracticed, false) : '-'
    const masteredDate = unit.masteredDate ? formatDate(unit.masteredDate, false) : '-'

    const baseText = `The activity ${unit.name} was first introduced to ${learner.fullName} on ${firstActivityDate}, with an initial score of ${firstScore}%.`
    const statusText = `${learner.fullName}'s progress status in this activity is ${readableStatus}.`

    let progressStoryText = ''

    if (sessionCount >= 3) {
      const recentScores = (unit.last3Scores || [])
        .slice()
        .reverse()
        .map((score) => `${formatScore(score)}%`)
        .join(', ')

      progressStoryText = `${baseText} During the last ${durationText}, ${learner.fullName} engaged with the activity ${sessionCount} times. In the most recent three sessions, they received scores of ${recentScores}. ${statusText}`
    } else if (sessionCount === 2) {
      const twoScores = (unit.last3Scores || [])
        .slice(-2)
        .reverse()
        .map((score) => `${formatScore(score)}%`)
        .join(' and ')

      progressStoryText = `${baseText} During the last ${durationText}, the activity was practiced twice, with scores of ${twoScores}. ${statusText}`
    } else if (sessionCount === 1) {
      const lastScore = unit.last3Scores?.length ? `${formatScore(unit.last3Scores[unit.last3Scores.length - 1])}%` : '-'

      progressStoryText = `${baseText} During the last ${durationText}, only one session was completed, with a score of ${lastScore}. ${statusText}`
    } else {
      progressStoryText = `${baseText} No sessions were recorded during the last ${durationText}. ${statusText}`
    }

    const sortedEntries = unit.scoresData ? Object.entries(unit.scoresData).sort(([a], [b]) => safeTime(a) - safeTime(b)) : []

    const chartDataObj = {
      labels: sortedEntries.map(([date]) => formatDate(date, true, true)),
      datasets: [
        {
          label: '% score',
          data: sortedEntries.map(([, score]) => score),
          ...chartConfig,
        },
      ],
    }

    return {
      progressStory: progressStoryText,
      chartData: chartDataObj,
      formattedDates: {
        firstActivityDate,
        lastPracticed,
        masteredDate,
        readableStatus,
      },
    }
  }, [unit, learner.fullName, duration])

  if (!unit || !learner?.fullName) {
    return (
      <div className={styles.activityPdfContainer}>
        <p>Unable to load activity data</p>
      </div>
    )
  }

  return (
    <div className={styles.activityPdfContainer}>
      <div id='toPdfReport' className={cn(styles.pdfAdjust)}>
        <h3 className={styles.unitTitle}>{unit.name || 'Unnamed Activity'}</h3>
        <div className={styles.reportActivitySummary}>
          <p className={styles.summaryHeader}>Status</p>
          <p className={styles.summaryHeader}>First Date Introduced</p>
          <p className={styles.summaryHeader}>Last Practiced</p>
          <p className={styles.summaryHeader}>Practiced sessions</p>
          <p className={styles.summaryHeader}>Mastery Date</p>
          <p>{mapStatusToReadableStatus(unit.status as TaskStatus)}</p>
          <p>{formattedDates.firstActivityDate}</p>
          <p>{formattedDates.lastPracticed}</p>
          <p>{unit.activityCount || 0}</p>
          <p>{formattedDates.masteredDate}</p>
        </div>
      </div>

      {unit.activityObjective && (
        <div id='toPdfReport' className={styles.pdfAdjust}>
          <h5>Activity Objective:</h5>
          <p>{unit.activityObjective}</p>
        </div>
      )}

      <div id='toPdfReport' className={styles.pdfAdjust}>
        <h5>Progress Story:</h5>
        <p>{progressStory}</p>
      </div>

      {chartData.labels.length > 0 && (
        <div id='toPdfReport'>
          <h5 className={styles.pdfAdjust}>Progress Graph:</h5>
          <div className={styles.chartContainer}>
            <Line id='activityChart' options={chartOptions} data={chartData} aria-label={`Progress chart for ${unit.name}`} />
          </div>
        </div>
      )}
    </div>
  )
}
